/**
 * Workflows SD-SC-12 (CONN-004, CONN-026): the `http_request` step as "Call connector operation". Mirrors the backend
 * `app/core/constants/workflow_connector_op.py`; the server is the judge, these let the builder refuse a mistake first.
 *
 * One step, two forms. The legacy inline form names a webhook endpoint (`endpoint_id` + method + path + map). The operation
 * form names a connector and one of its operations (`connector_id` + `operation_key`) and only fills its typed `params` and
 * says which declared `outputs` become which run variable.
 */

export const CONNECTOR_OP_KEY_PATTERN = /^[a-z][a-z0-9_]{0,59}$/;
/** A parameter name as the operation's JSON Schema declares it. */
export const CONNECTOR_OP_PARAM_NAME_PATTERN = /^[A-Za-z_][A-Za-z0-9_]{0,59}$/;
/** A declared output is a `variable` field of the operation's response mapping, and so is the run variable it is copied into. */
export const CONNECTOR_OP_OUTPUT_PATTERN = /^var\.[a-z0-9_]{1,40}$/;
export const CONNECTOR_OP_LIMITS = { params: 30, paramValue: 1000, outputs: 20, version: 10_000 } as const;

/** Fields of the legacy inline form: a step that names a connector sets none of them. */
export const CONNECTOR_OP_LEGACY_ONLY_FIELDS = ["method", "path", "query", "headers", "body", "timeout_ms", "map"] as const;
/** Fields of the operation form: a legacy step sets none of them. */
export const CONNECTOR_OP_ONLY_FIELDS = ["operation_key", "version", "params", "outputs", "not_found_goto", "rejected_goto", "unavailable_goto"] as const;

/** The optional exits of an operation's failure; each falls back to `failure_goto`, and that one to the next step. */
export const CONNECTOR_ERROR_EXITS = ["not_found_goto", "rejected_goto", "unavailable_goto"] as const;
export type ConnectorErrorExit = (typeof CONNECTOR_ERROR_EXITS)[number];

/** Canonical error codes (CONN-022) and the exit each leaves by; a code an operation defines itself takes `failure_goto`. */
export const CONNECTOR_ERROR_EXIT_OF: Readonly<Record<string, ConnectorErrorExit>> = {
  not_found: "not_found_goto",
  validation: "rejected_goto",
  conflict: "rejected_goto",
  out_of_stock: "rejected_goto",
  auth_failed: "unavailable_goto",
  rate_limited: "unavailable_goto",
  upstream_error: "unavailable_goto",
  too_large: "unavailable_goto",
};

/** `step.<name>.ok` / `.status` / `.error` are readable by the steps after an operation call. */
export const CONNECTOR_OP_FACTS = ["ok", "status", "error"] as const;

/** The condition fields of a `connector_event_received` run (`connector.data.<name>` is open, like the run variables). */
export const CONNECTOR_EVENT_FIELDS = ["connector.event", "connector.id", "connector.hook"] as const;
export const CONNECTOR_DATA_FIELD_PATTERN = /^connector\.data\.[a-z][a-z0-9_]{0,39}$/;
export const CONNECTOR_DATA_FIELD_PREFIX = "connector.data.";
